import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationTriangle as WarningIcon } from 'react-icons/fa';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Select, Typography } from '@mui/material';

import { DEFAULT_LANGUAGE_CODE } from 'src/constants/language';
import { useCustomizing } from 'src/components/app/context/CustomizingContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import Modal from 'src/components/common/Modal';

import { styles } from './index.styles';

export const LANGUAGE_SWITCHER_TEST_ID = 'language_switcher';

export interface LanguageSwitcherProps {
  color?: 'secondary' | 'neutral';
  languageOverride?: string[];
}

const LanguageSwitcher: FC<LanguageSwitcherProps> = ({
  color = 'neutral',
  languageOverride,
}) => {
  const { i18n, t } = useTranslation('common');
  const [errorMessage, setErrorMessage] = useState('');
  const { appLanguages, loading: customizingIsLoading } = useCustomizing();
  const languagesSelection = useMemo(
    () => languageOverride || appLanguages,
    [languageOverride, appLanguages],
  );
  const { changeLanguage } = useUserContext();
  const changeLanguageAndReload = ({ target }: SelectChangeEvent) => {
    changeLanguage(target.value).then((hasChanged) => {
      if (hasChanged) {
        // Reload page after language change to also refresh content from backend
        window.location.reload();
      }
    });
  };

  useEffect(() => {
    if (!languagesSelection.length && customizingIsLoading) return;
    if (!languagesSelection.find((code) => code === i18n.language)) {
      // change language in case a module does not support the selected language
      languagesSelection.length
        ? changeLanguage(languagesSelection[0])
        : changeLanguage(DEFAULT_LANGUAGE_CODE);
      return;
    }
  }, [customizingIsLoading, i18n.language, languagesSelection, changeLanguage]);

  const selectMenuProps = {
    PaperProps: {
      sx: styles.menuProps(color),
    },
  };

  if (!languagesSelection.length) return null;

  return (
    <>
      <Select
        value={i18n.language}
        onChange={changeLanguageAndReload}
        sx={styles.languageSelector(color)}
        IconComponent={KeyboardArrowDownRounded}
        data-test-id={LANGUAGE_SWITCHER_TEST_ID}
        MenuProps={selectMenuProps}
      >
        {languagesSelection.map((langCode) => (
          <MenuItem key={langCode} sx={styles.menuItem(color)} value={langCode}>
            <Typography variant="body3" sx={styles.menuItemLabel}>
              {langCode}
            </Typography>
          </MenuItem>
        ))}
      </Select>
      {errorMessage && (
        <Modal
          headline={t('error')}
          icon={<WarningIcon size={23} />}
          isOpen={!!errorMessage}
          onClose={() => setErrorMessage('')}
        >
          {errorMessage}
        </Modal>
      )}
    </>
  );
};

export default LanguageSwitcher;
